/**
 * Type-safe Amplitude tracking helpers
 *
 * Conventions:
 * - Use snake_case for all event names and properties
 * - Use IDs (not emails or names) for user references
 * - Include business-relevant context in properties
 */

import { trackEvent } from "./amplitude";


export const tracking = {
  // ============================================
  // 5.1. Acquisition & Onboarding
  // ============================================

  userSignedUp: (props: {
    user_role: "advisor" | "achiever" | "lead" | string;
    signup_code?: string | null;
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    ref_id?: string | null;
  }) => trackEvent("user_signed_up", props),

  userLoggedIn: (props: {
    user_id: string;
    user_role: "advisor" | "achiever" | "lead" | string;
    login_method: "email" | "linkedin";
  }) => trackEvent("user_logged_in", props),

  userLoggedOut: (props: {
    user_id: string;
    user_role: "advisor" | "achiever" | "lead" | string;
    logout_method: "manual" | "timeout" | "other" | string;
  }) => trackEvent("user_logged_out", props),

  profileStepCompleted: (props: {
    step_name: "current_role" | "bio" | "experience" | "preferences" | string;
    user_role: "advisor" | "achiever" | string;
  }) => trackEvent("profile_step_completed", props),

  profileCompleted: (props: {
    time_to_complete_seconds: number;
    user_role: "advisor" | "achiever" | string;
  }) => trackEvent("profile_completed", props),

  // ============================================
  // 5.2. Activation & PAB Engagement
  // ============================================

  pabInvitationSent: (props: {
    pab_session_id: number;
    achiever_user_id: string;
    advisor_id: string;
    match_score: number;
  }) => trackEvent("pab_invitation_sent", props),

  pabInvitationViewed: (props: { pab_session_id: number; achiever_user_id: string }) =>
    trackEvent("pab_invitation_viewed", props),

  pabInvitationAccepted: (props: { pab_session_id: number; time_to_respond_hours: number }) =>
    trackEvent("pab_invitation_accepted", props),

  pabInvitationDeclined: (props: {
    pab_session_id: number;
    decline_reason: "too_busy" | "not_good_fit" | "conflict" | "other" | string;
  }) => trackEvent("pab_invitation_declined", props),

  goalCreated: (props: {
    goal_id: number;
    owner_user_id: string;
    pab_session_id: number;
    goal_title: string;
    goal_type: "okr" | "milestone" | string;
    goal_weight: number;
  }) => trackEvent("goal_created", props),

  goalUpdated: (props: {
    goal_id: number;
    owner_user_id: string;
    pab_session_id: number;
    updated_fields: string[];
  }) => {
    return trackEvent("goal_updated", {
      goal_id: props.goal_id,
      owner_user_id: props.owner_user_id,
      pab_session_id: props.pab_session_id,
      updated_fields: props.updated_fields.join(","),
    });
  },

  oktCreated: (props: {
    okt_id: number;
    goal_id: number;
    owner_user_id: string;
    pab_session_id: number;
    okt_title: string;
    due_date?: string | null;
  }) => trackEvent("okt_created", props),

  oktUpdated: (props: { okt_id: number; owner_user_id: string; fields: string[] }) =>
    trackEvent("okt_updated", {
      okt_id: props.okt_id,
      owner_user_id: props.owner_user_id,
      fields: props.fields.join(","),
    }),

  // ============================================
  // 5.3. Ongoing Engagement & Feedback
  // ============================================

  dashboardViewed: (props: { user_id?: string | null; pab_session_id?: number | null }) =>
    trackEvent("dashboard_viewed", props),

  goalProgressUpdated: (props: {
    goal_id: number;
    owner_user_id: string;
    pab_session_id: number;
    previous_progress: number;
    new_progress: number;
    progress_delta: number;
  }) => trackEvent("goal_progress_updated", props),

  goalStatusUpdated: (props: {
    goal_id: number;
    owner_user_id: string;
    pab_session_id: number;
    previous_status: "active" | "completed" | "archived" | string;
    new_status: "active" | "completed" | "archived" | string;
  }) => trackEvent("goal_status_updated", props),

  advisorProfileViewed: (props: {
    advisor_user_id: string;
    viewer_user_id: string;
    source: "matching" | "directory" | "link" | string;
    time_on_page_seconds?: number;
  }) => trackEvent("advisor_profile_viewed", props),

  advisorMatchingComplete: (props: {
    achiever_user_id: string;
    algorithm_version: string;
    candidates_count: number;
    top_match_advisor_user_id: string;
    match_latency_ms: number;
  }) => trackEvent("advisor_matching_complete", props),

  matchingComplete: (props: {
    pab_session_id: number;
    achiever_user_id: string;
    advisors_count: number;
    top_match_advisor_id?: string | null;
  }) => trackEvent("matching_complete", props),

  groupSessionScheduled: (props: {
    session_id: number;
    organizer_user_id: string;
    participants: string[];
    scheduled_time: string;
  }) => {
    return trackEvent("group_session_scheduled", {
      session_id: props.session_id,
      organizer_user_id: props.organizer_user_id,
      participants: props.participants.join(","),
      scheduled_time: props.scheduled_time,
    });
  },

  meetingJoined: (props: { session_id: number; user_id: string; meeting_id: number }) =>
    trackEvent("meeting_joined", props),

  oktStatusUpdated: (props: {
    okt_id: number;
    owner_user_id: string;
    previous_status: string;
    new_status: string;
  }) => trackEvent("okt_status_updated", props),

  oktCompleted: (props: {
    okt_id: number;
    goal_id: number;
    owner_user_id: string;
    pab_session_id: number;
    time_to_complete_days: number;
  }) => trackEvent("okt_completed", props),

  messageSent: (props: {
    message_id: number;
    thread_id: number;
    sender_user_id: string;
    recipient_count: number;
    channel: "in_app" | "email";
    message_type: "text" | "rich_text" | string;
    has_attachments: boolean;
    message_length: number;
  }) => trackEvent("message_sent", props),

  messagesOpen: (props: {
    sender_id: string;
    message_id: number;
    thread_id: number;
    viewer_user_id: string;
    opened_after_minutes: number;
  }) => trackEvent("messages_open", props),

  emailSent: (props: {
    email_id: number;
    template: string;
    sender_user_id: string;
    recipient_count: number;
    subject: string;
    utm_source?: string | null;
  }) => trackEvent("email_sent", props),

  emailOpened: (props: {
    email_id: number;
    template: string;
    recipient_user_id: string;
    opened_after_seconds: number;
    client: "gmail" | "outlook" | "apple_mail" | string;
  }) => trackEvent("email_opened", props),

  emailClicked: (props: {
    email_id: number;
    campaign_id: string;
    template_id: string;
    recipient_user_id: string;
    link_url: string;
    link_label: string;
    clicked_after_seconds: number;
  }) => trackEvent("email_clicked", props),

  feedbackStarted: (props: {
    pab_session_id: number;
    recipient_user_id: string;
    recipient_role: "advisor" | "achiever";
    score: number;
    comment_length: number;
  }) => trackEvent("feedback_started", props),

  feedbackSubmitted: (props: {
    pab_session_id: number;
    recipient_user_id: string;
    recipient_role: "advisor" | "achiever";
    score: number; // NPS score 0-10
    comment_length: number;
  }) => trackEvent("feedback_submitted", props),

  // ============================================
  // Section Engagement
  // ============================================

  sectionEntered: (props: { section_id: string; page: string; user_id?: string | null }) =>
    trackEvent("section_entered", props),

  sectionExited: (props: {
    section_id: string;
    page: string;
    time_spent_seconds: number;
    user_id?: string | null;
  }) => trackEvent("section_exited", props),

  profileCardSectionEntered: (props: { page: string; user_id?: string | null }) =>
    trackEvent("profile_card_section_entered", props),

  profileCardSectionExited: (props: {
    page: string;
    time_spent_seconds: number;
    user_id?: string | null;
  }) => trackEvent("profile_card_section_exited", props),

  bioSectionEntered: (props: { page: string; user_id?: string | null }) =>
    trackEvent("bio_section_entered", props),

  bioSectionExited: (props: {
    page: string;
    time_spent_seconds: number;
    user_id?: string | null;
  }) => trackEvent("bio_section_exited", props),

  // ============================================
  // Additional Suggested Events
  // ============================================

  termsAndConditionsToggled: (props: { accepted: boolean; page: string }) =>
    trackEvent("terms_and_conditions_toggled", props),

  // ============================================
  // Signup CTAs
  // ============================================

  signupStepViewed: (props: { step: number; role: "achiever" | "advisor" | string }) =>
    trackEvent("signup_step_viewed", props),

  signupLinkedInClicked: (props: {
    role: "achiever" | "advisor" | string;
    has_org_code: boolean;
  }) => trackEvent("signup_linkedin_clicked", props),

  signupEmailMethodSelected: (props: { role: "achiever" | "advisor" | string }) =>
    trackEvent("signup_email_method_selected", props),

  signupFormSubmitted: (props: { role: "achiever" | "advisor" | string; has_org_code: boolean }) =>
    trackEvent("signup_form_submitted", props),

  signupVerificationEmailResent: (props: { role?: "achiever" | "advisor" | string | null }) =>
    trackEvent("signup_verification_email_resent", props),

  termsOfServiceClicked: (props: { page: string }) => trackEvent("terms_of_service_clicked", props),

  privacyPolicyClicked: (props: { page: string }) => trackEvent("privacy_policy_clicked", props),

  // ============================================
  // Login CTAs
  // ============================================

  loginLinkedInClicked: () => trackEvent("login_linkedin_clicked"),

  loginEmailSubmitted: () => trackEvent("login_email_submitted"),

  loginLinkResent: () => trackEvent("login_link_resent"),

  apiError: (props: { endpoint: string; status_code: number; error_message: string }) =>
    trackEvent("api_error", props),

  formValidationError: (props: { form_name: string; field_name: string; error_type: string }) =>
    trackEvent("form_validation_error", props),

  pageLoaded: (props: { page_name: string; load_time_ms?: number }) =>
    trackEvent("page_loaded", props),

  // Lead Flow Events

  leadEmailVerified: (props: { lead_id: number; time_to_verify_minutes: number }) =>
    trackEvent("lead_email_verified", props),

  leadMatchingComplete: (props: {
    lead_id: number;
    advisors_count: number;
    top_match_score: number;
    matching_duration_seconds: number;
    has_top_match: boolean;
  }) => trackEvent("lead_matching_complete", props),

  leadTopMatchTooltipViewed: (props: {
    lead_id: number;
    advisor_id: string;
    match_score: number;
  }) => trackEvent("lead_top_match_tooltip_viewed", props),

  leadBookDemoClicked: (props: {
    lead_id: number;
    click_source: "matching_page" | "matching_limit_screen";
    advisors_viewed_count: number;
    time_on_page_seconds: number;
  }) => trackEvent("lead_book_demo_clicked", props),

  leadMatchingLimitReached: (props: { lead_id: number; max_attempts: number }) =>
    trackEvent("lead_matching_limit_reached", props),

  // ============================================
  // FTUX (First Time User Experience)
  // ============================================

  ftuxStepCompleted: (props: { step_name: string; user_role: "advisor" | "achiever" | string }) =>
    trackEvent("ftux_step_completed", props),

  // ============================================
  // Getting Started (XO launch journey)
  //
  // One sender; every event name (getting_started_<...>) is built in
  // src/pages/gettingStarted/gettingStartedTracking.ts, which documents the
  // full list.
  // ============================================

  gettingStarted: (
    event: string,
    props?: Record<string, string | number | boolean | null | undefined>,
  ) => trackEvent(`getting_started_${event}`, props),

  // ============================================
  // Advisor FTUX & Platform Tour
  // ============================================

  advisorInviteLinkCopied: (props: { user_id: string | null }) =>
    trackEvent("advisor_invite_link_copied", props),

  advisorTourStarted: (props: { user_id?: string | null }) =>
    trackEvent("advisor_tour_started", props),

  tourStepViewed: (props: {
    step_index: number;
    step_label: string;
    target_id: string;
    user_id?: string | null;
  }) => trackEvent("tour_step_viewed", props),

  advisorTourCompleted: (props: { steps_viewed: number; user_id?: string | null }) =>
    trackEvent("advisor_tour_completed", props),

  advisorTourDismissed: (props: {
    step_index: number;
    step_label: string;
    user_id?: string | null;
  }) => trackEvent("advisor_tour_dismissed", props),

  // ============================================
  // Goals Page
  // ============================================

  taskNotesOpened: (props: {
    task_id: number;
    goal_id: number;
    notes_count: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("task_notes_opened", props),

  taskNoteAdded: (props: {
    task_id: number;
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("task_note_added", props),

  goalAddNewClicked: (props: { pab_session_id?: number | null; user_id?: string | null }) =>
    trackEvent("goal_add_new_clicked", props),

  goalCreateMethodSelected: (props: {
    method: "manual" | "ai_suggested";
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("goal_create_method_selected", props),

  goalEditOpened: (props: {
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("goal_edit_opened", props),

  goalDeleted: (props: {
    goal_id: number;
    goal_title: string;
    tasks_count: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("goal_deleted", props),

  goalMarkedAchieved: (props: {
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("goal_marked_achieved", props),

  taskAddOpened: (props: {
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("task_add_opened", props),

  taskEditOpened: (props: {
    task_id: number;
    goal_id: number;
    edit_mode: "inline" | "form";
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("task_edit_opened", props),

  // ============================================
  // PAB Page
  // ============================================

  pabViewFullProfileClicked: (props: {
    advisor_id: string;
    pab_session_id?: number | null;
    viewer_user_id?: string | null;
  }) => trackEvent("pab_view_full_profile_clicked", props),

  pabScheduleOneOnOneClicked: (props: {
    advisor_id: string;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("pab_schedule_one_on_one_clicked", props),

  pabMessageOpened: (props: { advisor_id: string; pab_session_id?: number | null }) =>
    trackEvent("pab_message_opened", props),

  pabMessageSent: (props: {
    pab_session_id: number;
    channel: "group" | "direct";
    message_length: number;
    user_role: string;
  }) => trackEvent("pab_message_sent", props),

  // ============================================
  // Advisor Dashboard — Requests Card
  // ============================================

  advisorRequestViewDetailsClicked: (props: {
    pab_session_id?: number | null;
    achiever_name?: string | null;
    user_id?: string | null;
  }) => trackEvent("advisor_request_view_details_clicked", props),

  advisorRequestAcceptClicked: (props: { pab_session_id: number; user_id?: string | null }) =>
    trackEvent("advisor_request_accept_clicked", props),

  advisorRequestDeclineSubmitted: (props: {
    pab_session_id: number;
    reason: string;
    user_id?: string | null;
  }) => trackEvent("advisor_request_decline_submitted", props),

  // ============================================
  // Advisor Dashboard — PAB Cards & Detail Page
  // ============================================

  advisorPabViewDetailsClicked: (props: {
    pab_session_id: number;
    source: "pabs_page" | "progress_card";
    user_id?: string | null;
  }) => trackEvent("advisor_pab_view_details_clicked", props),

  advisorPabMessageClicked: (props: {
    pab_session_id: number;
    source: "pabs_page" | "progress_card" | "detail_page";
    user_id?: string | null;
  }) => trackEvent("advisor_pab_message_clicked", props),

  advisorPabDetailNotesOpened: (props: {
    task_id: number;
    okr_id: number;
    pab_session_id: number;
    user_id?: string | null;
  }) => trackEvent("advisor_pab_detail_notes_opened", props),

  // ============================================
  // Profile Page — Calendar & Accounts
  // ============================================

  calendarConnectClicked: (props: {
    user_id?: string | null;
    user_role?: string | null;
    action: "connect" | "add_account";
  }) => trackEvent("calendar_connect_clicked", props),

  accountSwitchClicked: (props: { from_user_id?: string | null; to_user_id?: string | null }) =>
    trackEvent("account_switch_clicked", props),

  addAnotherAccountClicked: (props: { user_id?: string | null; user_role?: string | null }) =>
    trackEvent("add_another_account_clicked", props),

  // ============================================
  // Sessions Page
  // ============================================

  sessionPrepClicked: (props: {
    meeting_id: number;
    meeting_format: string;
    pab_session_id?: number | null;
    user_id?: string | null;
    user_role: string;
  }) => trackEvent("session_prep_clicked", props),

  sessionJoinClicked: (props: {
    meeting_id: number;
    meeting_format: string;
    pab_session_id?: number | null;
    user_id?: string | null;
    user_role: string;
  }) => trackEvent("session_join_clicked", props),

  sessionSummaryViewed: (props: {
    meeting_id: number;
    meeting_format: string;
    pab_session_id?: number | null;
    user_id?: string | null;
    user_role: string;
  }) => trackEvent("session_summary_viewed", props),

  // ============================================
  // Calendar (Step 4 — Availability)
  // ============================================

  calendarConnected: (props: { user_role: "achiever" | "advisor" | string }) =>
    trackEvent("calendar_connected", props),

  calendarDisconnected: (props: { user_role: "achiever" | "advisor" | string }) =>
    trackEvent("calendar_disconnected", props),

  // ============================================
  // Wrapup Flow
  // ============================================

  wrapupBannerCtaClicked: (props: {
    action: "start" | "continue";
    resume_step: number;
    user_id?: string | null;
  }) => trackEvent("wrapup_banner_cta_clicked", props),

  wrapupStepNextClicked: (props: {
    step: number;
    user_id?: string | null;
    program_external_id?: string | null;
  }) => trackEvent("wrapup_step_next_clicked", props),

  wrapupStepBackClicked: (props: {
    step: number;
    user_id?: string | null;
    program_external_id?: string | null;
  }) => trackEvent("wrapup_step_back_clicked", props),

  wrapupCompleted: (props: { user_id?: string | null; program_external_id?: string | null }) =>
    trackEvent("wrapup_completed", props),

  wrapupAdvisorRatingSubmitted: (props: {
    advisor_id: string;
    advisor_name: string;
    would_recommend: boolean | null;
    tags_count: number;
    has_written_feedback: boolean;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("wrapup_advisor_rating_submitted", props),

  wrapupWhatsNextClicked: (props: { user_id?: string | null }) =>
    trackEvent("wrapup_whats_next_clicked", props),

  // ============================================
  // STARs Page
  // ============================================

  starCaptureClicked: (props: {
    goal_id: number;
    goal_title: string;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_capture_clicked", props),

  starTutorialOpened: (props: {
    source: "header_button" | "empty_state_button";
    user_id?: string | null;
  }) => trackEvent("star_tutorial_opened", props),

  starViewGoalsClicked: (props: { user_id?: string | null }) =>
    trackEvent("star_view_goals_clicked", props),

  starDetailOpened: (props: {
    star_id: number;
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_detail_opened", props),

  starReviewClicked: (props: {
    star_id: number;
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_review_clicked", props),

  starSectionUpdated: (props: {
    star_id: number;
    section: "situation" | "task" | "action" | "result";
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_section_updated", props),

  starShareOrExportOpened: (props: {
    star_id: number;
    source: "header_button";
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_share_or_export_opened", props),

  starViewRelatedGoalClicked: (props: {
    star_id: number;
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_view_related_goal_clicked", props),

  starGoalDrawerGoToGoalClicked: (props: {
    star_id: number;
    goal_id: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_goal_drawer_go_to_goal_clicked", props),

  starShareLinkCopied: (props: {
    star_id?: number | null;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_share_link_copied", props),

  starShareTextCopied: (props: {
    star_id?: number | null;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_share_text_copied", props),

  starShareDownloadClicked: (props: {
    star_id?: number | null;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_share_download_clicked", props),

  starShareEmailClicked: (props: {
    star_id?: number | null;
    advisor_emails_count: number;
    pab_session_id?: number | null;
    user_id?: string | null;
  }) => trackEvent("star_share_email_clicked", props),

  // ============================================
  // Expert Program Flow
  // ============================================

  programDetailsViewed: (props: {
    program_external_id: string;
    source: "home_header" | "programs_list" | "dashboard_active_program_card" | string;
    user_id?: string | null;
  }) => trackEvent("program_details_viewed", props),

  programSignOffOpened: (props: {
    program_external_id?: string | null;
    user_role: "advisor" | "achiever" | string;
    user_id?: string | null;
  }) => trackEvent("program_sign_off_opened", props),

  programAdvisorMessageOpened: (props: {
    program_external_id?: string | null;
    advisor_user_id: string;
    user_id?: string | null;
  }) => trackEvent("program_advisor_message_opened", props),

  publicProgramEnrollClicked: (props: {
    program_id: string;
    role?: string | null;
    signup_code?: string | null;
  }) => trackEvent("public_program_enroll_clicked", props),

  programSavedAsDraft: (props: {
    program_external_id: string;
    okrs_count?: number;
    sessions_count?: number;
  }) => trackEvent("program_saved_as_draft", props),

  programPublished: (props: {
    program_external_id: string;
    source: "create_flow" | "programs_list";
    duration_weeks?: number | null;
    okrs_count?: number;
    sessions_count?: number;
    co_experts_count?: number;
  }) => trackEvent("program_published", props),

  /**
   * Expert submitted a program for review (DRAFT/REJECTED -> PENDING_REVIEW).
   * Distinct from programPublished, which now only fires when a program is
   * actually approved and goes live — conflating the two would make the
   * publish funnel look healthy while programs pile up unreviewed.
   */
  programSubmittedForReview: (props: {
    program_external_id: string;
    source: "create_flow" | "programs_list";
    duration_weeks?: number | null;
    okrs_count?: number;
    sessions_count?: number;
    co_experts_count?: number;
    is_resubmission?: boolean;
  }) => trackEvent("program_submitted_for_review", props),

  programCoExpertInviteOpened: (props: { program_external_id: string; source: string }) =>
    trackEvent("program_coexpert_invite_opened", props),

  programCoExpertInvited: (props: { program_external_id: string; invited_email?: string | null }) =>
    trackEvent("program_coexpert_invited", props),

  programAchieversInvited: (props: { program_external_id: string; invited_count: number }) =>
    trackEvent("program_achievers_invited", props),

  programEnrollmentLinkCopied: (props: { program_external_id: string; source: string }) =>
    trackEvent("program_enrollment_link_copied", props),

  sessionCreateOpened: (props: {
    program_external_id: string;
    session_type: "KICKOFF" | "WRAPUP" | "ADDITIONAL" | string;
    source: "create_flow" | "detail_page";
  }) => trackEvent("session_create_opened", props),

  sessionCreated: (props: {
    program_external_id: string;
    session_id: number;
    session_type: "KICKOFF" | "WRAPUP" | "ADDITIONAL" | string;
    source: "create_flow" | "detail_page";
  }) => trackEvent("session_created", props),

  programGoalCreated: (props: {
    program_external_id: string;
    goal_id: number;
    goal_title: string;
    tasks_count: number;
    creation_method: "ai" | "manual";
  }) => trackEvent("program_goal_created", props),

  sessionBooked: (props: { program_external_id: string; session_id?: number | string | null }) =>
    trackEvent("session_booked", props),

  sessionRescheduled: (props: { program_external_id: string; session_id?: number | string | null }) =>
    trackEvent("session_rescheduled", props),

  programMemberMessageOpened: (props: {
    program_external_id: string;
    membership_id: string;
    achiever_user_id?: string | null;
    user_id?: string | null;
  }) => trackEvent("program_member_message_opened", props),

  programCohortMessageOpened: (props: { program_external_id: string; source: string }) =>
    trackEvent("program_cohort_message_opened", props),

  programCohortMessageSent: (props: { program_external_id: string; message_length: number }) =>
    trackEvent("program_cohort_message_sent", props),

  programDirectMessageSent: (props: {
    program_id: string;
    recipient_user_id?: string | null;
    message_length: number;
  }) => trackEvent("program_direct_message_sent", props),

  programReportViewed: (props: { program_external_id: string }) =>
    trackEvent("program_report_viewed", props),

  programReportExported: (props: { program_external_id: string }) =>
    trackEvent("program_report_exported", props),

  programCreationStarted: (props: {
    source:
      | "programs_list_header"
      | "programs_list_empty_state"
      | "advisor_ftux"
      // "Draft your first program" on the redesigned XO Home.
      | "advisor_home"
      // "Create a Program" in the Getting Started side panel.
      | "getting_started"
      // The "What would you like to create?" chooser at /programs/new, which
      // now sits between the Programs list and either wizard.
      | "program_type_chooser";
    // Which format was picked at the chooser. Absent for the older sources,
    // which predate there being a choice.
    program_type?: "expert" | "peer_advisory" | "organisation";
    user_id?: string | null;
  }) => trackEvent("program_creation_started", props),

  programDraftContinued: (props: { program_external_id: string; source: "programs_list"; user_id?: string | null }) =>
    trackEvent("program_draft_continued", props),

  programArchived: (props: { program_external_id: string }) => trackEvent("program_archived", props),
};

// Export individual functions for convenience
export const {
  userSignedUp,
  userLoggedIn,
  userLoggedOut,
  profileStepCompleted,
  profileCompleted,
  pabInvitationSent,
  pabInvitationViewed,
  pabInvitationAccepted,
  pabInvitationDeclined,
  goalCreated,
  goalUpdated,
  oktCreated,
  oktUpdated,
  dashboardViewed,
  goalProgressUpdated,
  goalStatusUpdated,
  advisorProfileViewed,
  matchingComplete,
  groupSessionScheduled,
  meetingJoined,
  oktStatusUpdated,
  oktCompleted,
  messageSent,
  messagesOpen,
  emailSent,
  emailOpened,
  emailClicked,
  feedbackStarted,
  feedbackSubmitted,
  sectionEntered,
  sectionExited,
  profileCardSectionEntered,
  profileCardSectionExited,
  bioSectionEntered,
  bioSectionExited,
  termsAndConditionsToggled,
  apiError,
  formValidationError,
  pageLoaded,
  leadEmailVerified,
  leadMatchingComplete,
  leadTopMatchTooltipViewed,
  leadBookDemoClicked,
  leadMatchingLimitReached,
  advisorInviteLinkCopied,
  advisorTourStarted,
  tourStepViewed,
  advisorTourCompleted,
  advisorTourDismissed,
  signupStepViewed,
  signupLinkedInClicked,
  signupEmailMethodSelected,
  signupFormSubmitted,
  signupVerificationEmailResent,
  termsOfServiceClicked,
  privacyPolicyClicked,
  loginLinkedInClicked,
  loginEmailSubmitted,
  loginLinkResent,
  calendarConnected,
  calendarDisconnected,
  ftuxStepCompleted,
  pabViewFullProfileClicked,
  pabScheduleOneOnOneClicked,
  pabMessageOpened,
  pabMessageSent,
  goalAddNewClicked,
  goalCreateMethodSelected,
  goalEditOpened,
  goalDeleted,
  goalMarkedAchieved,
  taskAddOpened,
  taskEditOpened,
  taskNotesOpened,
  taskNoteAdded,
  sessionPrepClicked,
  sessionJoinClicked,
  sessionSummaryViewed,
  calendarConnectClicked,
  accountSwitchClicked,
  addAnotherAccountClicked,
  advisorRequestViewDetailsClicked,
  advisorRequestAcceptClicked,
  advisorRequestDeclineSubmitted,
  advisorPabViewDetailsClicked,
  advisorPabMessageClicked,
  advisorPabDetailNotesOpened,
  wrapupBannerCtaClicked,
  wrapupStepNextClicked,
  wrapupStepBackClicked,
  wrapupCompleted,
  wrapupAdvisorRatingSubmitted,
  wrapupWhatsNextClicked,
  starCaptureClicked,
  starTutorialOpened,
  starViewGoalsClicked,
  starDetailOpened,
  starReviewClicked,
  starSectionUpdated,
  starShareOrExportOpened,
  starViewRelatedGoalClicked,
  starGoalDrawerGoToGoalClicked,
  starShareLinkCopied,
  starShareTextCopied,
  starShareDownloadClicked,
  starShareEmailClicked,
  programDetailsViewed,
  programSignOffOpened,
  programAdvisorMessageOpened,
  publicProgramEnrollClicked,
  programSavedAsDraft,
  programPublished,
  programSubmittedForReview,
  programCoExpertInviteOpened,
  programCoExpertInvited,
  programAchieversInvited,
  programEnrollmentLinkCopied,
  sessionCreateOpened,
  sessionCreated,
  programGoalCreated,
  sessionBooked,
  sessionRescheduled,
  programMemberMessageOpened,
  programCohortMessageOpened,
  programCohortMessageSent,
  programDirectMessageSent,
  programReportViewed,
  programReportExported,
  programCreationStarted,
  programDraftContinued,
  programArchived,
} = tracking;
